import { VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import { useInjection } from 'inversify-react';
import { useEffect } from 'react';

import { Page, useProjects } from '@desygner/ui-features-projects';

import { TYPES } from '../../../../inversify.types';
import { IViewerStoreInjector } from '../../../../store';
import { DynamicSizeSkeleton } from '../../../components/Skeleton/DynamicSizeSkeleton';
import { useLoadPageContent } from '../../../hooks/useLoadPageContent';
import { ShadowRootContent } from '../ShadowRootContent/ShadowRootContent';
import { StyledPageItemWrapper } from '../Viewer.styles';
import { StyledVirtualPageWrapper } from './MultiPageMode.styles';

export type MultiPageModeProps = {
  item: Page;
  virtualItem: VirtualItem;
  virtualizer?: Virtualizer<Window, Element>;
};

export const MultiPageMode = ({
  item,
  virtualItem: { index, size, start, key },
  virtualizer,
}: MultiPageModeProps) => {
  const useViewerStore = useInjection<IViewerStoreInjector>(TYPES.Store.Viewer);
  const { pages, project } = useProjects();
  const zoomLevel = useViewerStore((state) => state.zoom / 100);

  const { isProcessingPageContent } = useLoadPageContent({
    pageId: item.id,
    page: item,
  });
  const pageData = pages[key.toString()] as Page | undefined;
  const projectData = project?.pages[index];

  useEffect(() => {
    if (!pageData?.height) return;

    virtualizer?.resizeItem(index, pageData.height * zoomLevel);
  }, [index, pageData?.height, virtualizer, zoomLevel]);

  const computedWidth = (pageData?.width || projectData?.width || 0) * zoomLevel;
  const computedHeight = (pageData?.height || projectData?.height || 0) * zoomLevel;

  const shouldShowSkeleton = isProcessingPageContent || !pageData?.content;

  return (
    <StyledVirtualPageWrapper
      data-page-number={item.page_number || index + 1}
      style={{
        width: `${computedWidth}px`,
        height: `${size}px`,
        transform: `translateY(${start}px)`,
      }}
    >
      <StyledPageItemWrapper
        style={{
          width: `${computedWidth}px`,
          height: `${computedHeight}px`,
        }}
      >
        {shouldShowSkeleton ? (
          <DynamicSizeSkeleton height={computedHeight} width={computedWidth} />
        ) : (
          <ShadowRootContent content={pageData.content} pageId={item.id} />
        )}
      </StyledPageItemWrapper>
    </StyledVirtualPageWrapper>
  );
};
